import styled, { css } from "styled-components";
import type { OffsetAlignType } from "../../hooks/usePopoverTooltip";
import { getBreakpoint } from "../../themes/common";
import { type ColorType, theme } from "../v2/toyota/theme/toyotaTheme";
import * as Button from "./Button";

export const ARROW_HORIZONTAL_OFFSET = 30;
export const ARROW_SIZE = 9;

/**
 * Since iOS version 13 (iPadOS), Safari started zooming out when elements are rendered outside of the viewport
 * This breaks the logic of our dynamic popover which is only moved to a valid position after the first render, causing Safari to zoom out.
 *
 * To fix this we wrap the tooltip in a div with width=0 on initial render,
 * since the actual tooltip also has a fixed width this does not interfere with the position calculations.
 *
 * See OR-3596
 */
export const BoxWrapper = styled.div<{ hide?: boolean }>`
    position: relative;
    width: ${({ hide }) => (hide ? "0px" : "initial")};
    overflow: ${({ hide }) => (hide ? "hidden" : "initial")};
`;

type TooltipPropsType = {
    absolute?: boolean;
    point?: OffsetAlignType;
    visible?: boolean;
    offset?: { x: number; y: number };
    small?: boolean;
    disabled?: boolean;
};

const SmallTooltipStyles = css`
    min-width: 200px;
    padding: ${theme.space[2]}px;
`;

const NormalTooltipStyles = css`
    width: 300px;
    padding: ${theme.spacer}px;
    @media ${getBreakpoint("up", "sm")} {
        width: 325px;
    }
`;

export const Wrapper = styled.div<TooltipPropsType>`
    display: ${({ disabled }) => (disabled ? "none" : "block")};
    position: ${({ absolute }) => (absolute ? "absolute" : "relative")};
    min-width: 250px;
    padding: 20px 30px;
    font-family: ${theme.fonts.regular};
    font-size: 1.3rem;
    font-weight: 400;
    line-height: 1.8;
    text-align: start;
    text-shadow: none;
    text-transform: none;
    color: ${theme.colors.primaryBlack};
    background: ${theme.colors.primaryWhite};
    border-radius: 4px;
    box-shadow: 0 10px 15px 1px rgba(0, 0, 0, 0.25);
    transition: opacity 0.15s;
    pointer-events: ${(props) => (props.visible === undefined || props.visible === true ? "auto" : "none")};
    opacity: ${(props) => (props.visible === undefined || props.visible === true ? 1 : 0)};
    z-index: ${theme.zIndex.tooltip};

    ${({ offset }) =>
        offset &&
        css`
            transform: translate(${offset.x}px, ${offset.y}px);
        `};

    ${(props) => (props.small ? SmallTooltipStyles : NormalTooltipStyles)};

    ${({ point }) =>
        point &&
        css`
            /* Add a margin for the pointer mouseLeave events. */
            margin-bottom: ${ARROW_SIZE + 20}px;
            &::after {
                display: block;
                position: absolute;
                width: 0;
                height: 0;
                content: " ";
                border-left: ${ARROW_SIZE}px solid transparent;
                border-right: ${ARROW_SIZE}px solid transparent;
                border-${point.vertical === "top" ? "bottom" : "top"}: ${ARROW_SIZE}px solid ${
                    theme.colors.primaryWhite
                };
                ${point.horizontal}: ${ARROW_HORIZONTAL_OFFSET}px;
                ${point.vertical}: ${-ARROW_SIZE}px;
                }
        `};

    p {
        text-indent: 0;
    }
`;

export const Close = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;

    html[dir="rtl"] & {
        left: 10px;
        right: auto;
    }
`;

export const HitBoxButtonSpacer = styled.div`
    position: absolute;
    top: -10px;
    left: -15px;
    width: 50px;
    height: 50px;

    html[dir="rtl"] & {
        left: auto;
        right: -15px;
    }
`;

export const PopoverButton = styled(Button.Link)<{ iconColor?: ColorType }>`
    position: relative;
    padding: 0;
    svg {
        color: ${({ iconColor }) => (iconColor ? theme.colors[iconColor] || theme.colors.grey4 : theme.colors.grey4)};
    }
`;
